import { useMemo } from 'react';
import {
  func,
  number,
  bool,
  object,
  string,
  shape,
  instanceOf,
  any,
} from 'prop-types';
import styled from 'styled-components/macro';
import delve from 'dlv';

import themeColors from 'styles/themes/colors';
import modularScale from 'styles/helpers/modular-scale';

import getEntityKey from 'helpers/getEntityKey';
import formatDate from 'helpers/formatDate';
import { round } from './helpers/math';

import selectedEntitiesPropType from './prop-types/selectedEntities';
import periodPropType from './prop-types/period';
import TrackerTooltip from './TrackerTooltip/TrackerTooltip';

const TrackerBackground = styled('rect')`
  fill: ${({ isGhostTracker, isStrictDesign }) =>
    isGhostTracker || isStrictDesign
      ? themeColors.Grey
      : themeColors.AccentGreen};
  fill-opacity: ${({ isGhostTracker }) => (isGhostTracker ? 0.25 : 0.15)};
`;

const TrackerLine = styled('line')`
  stroke: ${({ theme, isGhostTracker, isStrictDesign }) =>
    isStrictDesign || isGhostTracker ? theme.CasperGrey : theme.AccentGreen};
  stroke-width: ${({ isGhostTracker, isStrictDesign }) =>
    isStrictDesign ? 2 : isGhostTracker ? 1 : 2};
`;

const TrackerDot = styled('circle')`
  stroke: ${({ theme, isGhostTracker, isStrictDesign }) =>
    isStrictDesign
      ? 'transparent'
      : isGhostTracker
      ? theme.CasperGrey
      : theme.AccentGreen};
  stroke-width: ${({ isGhostTracker }) => (isGhostTracker ? 1 : 2)};
  fill: ${({ theme, isStrictDesign }) =>
    isStrictDesign ? 'transparent' : theme.White};
`;

const Pointer = styled('circle')`
  stroke: white;
  stroke-width: ${({ isGhostTracker, isStrictDesign }) =>
    isStrictDesign ? 1.5 : isGhostTracker ? 0 : 2};
  fill: ${({ fillColor, isGhostTracker, theme }) =>
    isGhostTracker ? theme.CasperGrey : fillColor};
`;

const TrackerText = styled('text')`
  font-size: ${modularScale(-2)};
  font-weight: 600;
  font-family: inherit;
  fill: ${({ theme }) => theme.DeepBlue};
`;

/**
 * Pointers renders pointer dots on the currently selected entity data points
 *
 * @param {object} props React props
 * @param {function} props.colorScale configured D3.js color scale
 * @param {string} props.dataKey what key to use to access data values
 * @param {array} props.entities The entities to render a Pointer for
 * @param {boolean} props.isGhostTracker is this a ghost tracker?
 * @param {function} props.x A configured D3.js scaleTime
 * @param {function} props.y A configure D3.js scaleLinear
 *
 * @returns {React.Component} JSX Components that render to svg circle elements.
 */
function Pointers(props) {
  const {
    colorScale,
    dataKey,
    entities,
    isGhostTracker,
    x,
    y,
    isStrictDesign,
    weekNumber,
    viewType,
  } = props;

  const entitiesLength = entities?.length;
  const dontShowPointers = useMemo(
    () => viewType === 'organic/programmed' && entitiesLength > 3,
    [viewType, entitiesLength]
  );

  if (viewType && dontShowPointers) {
    return null;
  }

  return entities.map((entity) => {
    if (!entity || !entity.data || !entity.data.date) {
      return null;
    }

    const yValue = delve(entity.data, dataKey);
    const xCoord =
      typeof weekNumber === 'number' ? x(weekNumber) : x(entity.data.date);
    const yCoord = isStrictDesign && entity.y ? entity.y(yValue) : y(yValue);
    const fillColor = colorScale(getEntityKey(entity));
    return delve(entity.data, dataKey) === null ? null : (
      <Pointer
        key={`${isGhostTracker ? 'ghost-' : ''}selected-dot-${getEntityKey(
          entity
        )}`}
        cx={xCoord}
        cy={yCoord}
        r="5"
        fillColor={fillColor}
        isGhostTracker={isGhostTracker}
        isStrictDesign={isStrictDesign}
      />
    );
  });
}

/**
 * Tracker renders a colored or grey tracker based on its props.
 * It shows what datapoint the user is currently interacting with
 * or which datapoint the user currently has selected.
 *
 * @param {object} props React props
 * @param {function} props.band configured D3.js scaleBand
 * @param {string} props.clipPathId id for the tracker's clipping path
 * @param {function} props.colorScale configured D3.js color scale
 * @param {string} props.dataKey what key to use to access data values
 * @param {boolean} props.hasComplementary does this tracker's linechart have a complementary linechart?
 * @param {number} props.height the height of the parent linechart
 * @param {boolean} props.isDragging is the user currently touch dragging in the linechart?
 * @param {boolean} props.isGhostTracker is this a ghost tracker?
 * @param {string} props.label sales type label for the tooltip
 * @param {object} props.margin D3 conventional margin object
 * @param {('day'|'week'|'month'|'year')} props.period The interval of the displayed data
 * @param {array} props.selectedEntities The currently selected entities
 * @param {array} props.allEntities The currently selected entities
 * @param {boolean} props.showTooltip Should the tooltip be displayed?
 * @param {number} props.width The width of the parent linechart
 * @param {function} props.x A configured D3.js scaleTime
 * @param {function} props.y A configure D3.js scaleLinear
 * @param {number} props.yStart The topmost vertical tick position
 */
export default function Tracker(props) {
  const {
    band,
    clipPathId,
    colorScale,
    dataKey,
    hasComplementary,
    height,
    isDragging,
    isGhostTracker = false,
    label,
    margin,
    period,
    selectedEntities,
    allEntities,
    showTooltip,
    textBackgroundFilterId,
    tooltipRoot,
    width,
    x,
    y,
    yStart = 0,
    isStrictDesign = false,
    trackerLineStart,
    weekNumber,
    viewType,
  } = props;

  const tracker = selectedEntities?.find((entity) => entity.data !== undefined);

  if (!tracker?.data) return null;

  const strictDesignWidth = 8;
  const bandwidth = isStrictDesign
    ? strictDesignWidth
    : round(band.bandwidth());
  const xPosition =
    typeof weekNumber === 'number'
      ? round(x(weekNumber))
      : round(x(tracker.data.date));

  // Pre-calculate some offsets and settings for the tracker
  let trackerTextAlign = 'middle';

  // Left 10% of the graph
  if (xPosition < width * 0.1) {
    trackerTextAlign = 'start';
  }

  // Right 10% of the graph
  if (xPosition > width * 0.9) {
    trackerTextAlign = 'end';
  }

  return (
    <>
      <g clipPath={`url(#${clipPathId})`}>
        <TrackerBackground
          width={bandwidth}
          height={height}
          x={round(xPosition - bandwidth / 2)}
          y={yStart}
          isGhostTracker={isGhostTracker}
          isStrictDesign={isStrictDesign}
        />
      </g>

      <TrackerLine
        x1={xPosition}
        x2={xPosition}
        y1={isStrictDesign && !trackerLineStart ? 0 : yStart}
        y2={height}
        isGhostTracker={isGhostTracker}
        isStrictDesign={isStrictDesign}
      />

      {!hasComplementary && (
        <TrackerDot
          cx={xPosition}
          cy={height}
          r={isGhostTracker ? 3 : 5}
          isGhostTracker={isGhostTracker}
          isStrictDesign={isStrictDesign}
        />
      )}

      <Pointers
        colorScale={colorScale}
        dataKey={dataKey}
        entities={selectedEntities}
        viewType={viewType}
        isGhostTracker={isGhostTracker}
        x={x}
        y={y}
        isStrictDesign={isStrictDesign}
        weekNumber={weekNumber}
      />

      {showTooltip && (
        <TrackerTooltip
          colorScale={colorScale}
          dataKey={dataKey}
          isDragging={isDragging}
          label={label}
          margin={margin}
          height={height}
          tooltipRoot={tooltipRoot}
          trends={selectedEntities}
          allEntities={allEntities}
          weekNumber={weekNumber}
          x={x}
          y={y}
        />
      )}

      {!hasComplementary && (
        <TrackerText
          x={xPosition}
          y={height + 24}
          textAnchor={trackerTextAlign}
          filter={`url(#${textBackgroundFilterId})`}
        >
          &nbsp;
          {typeof weekNumber === 'number'
            ? `Week ${weekNumber}`
            : formatDate(tracker.data.date, period)}
          &nbsp;
        </TrackerText>
      )}
    </>
  );
}

Tracker.propTypes = {
  band: func.isRequired,
  clipPathId: string.isRequired,
  colorScale: func.isRequired,
  dataKey: string,
  hasComplementary: bool,
  height: number.isRequired,
  isDragging: bool,
  isGhostTracker: bool,
  label: string,
  margin: object,
  period: periodPropType.isRequired,
  selectedEntities: selectedEntitiesPropType.isRequired,
  allEntities: any,
  showTooltip: bool,
  textBackgroundFilterId: string.isRequired,
  tooltipRoot: shape({ current: instanceOf(Element) }),
  width: number.isRequired,
  x: func.isRequired,
  y: func.isRequired,
  yStart: number,
  isStrictDesign: bool,
  trackerLineStart: bool,
  weekNumber: number,
  viewType: string,
};
